import { ChangeDetectorRef, Component, DestroyRef, effect, input, ChangeDetectionStrategy } from "@angular/core";
import { ImportType } from "./import.component";
import { Bind } from "../../decorators/bind.decorator";
import { RowData } from "../s25-spreadsheet/types/sheet.js.types.expanded";
import { ImportBudget } from "./import.budget";
import { ImportService } from "./import.service";
import { ScrollLockService } from "../../services/scroll.lock.service";
import { BudgetImportSpreadsheetUtil } from "./util/budget/budget.import.spreadsheet.util";
import { SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";
import { S25Util } from "../../util/s25-util";
import { ImportTelemetry } from "./import.telemetry";
import type { TelemetrySession } from "../../services/telemetry/telemetry.session";

@Component({
    selector: "s25-ng-import-budgets",
    host: { class: "s25-ng-import-budgets" },
    template: `
        <div class="c-margin-bottom--single">
            Upload a spreadsheet with nested organization budgets, budget plans, and line items. Use a file exported
            from Budget Admin or the template below; read-only columns such as spent, committed, and plan status are
            ignored on import.
        </div>

        <div class="c-margin-bottom--single c-margin-top--single">
            <button class="aw-button aw-button--outline" (click)="downloadTemplate()">Download Excel Template</button>
        </div>

        <div class="c-margin-bottom--single c-margin-top--single">
            <details class="c-margin-bottom--single">
                <summary class="c-margin-bottom--single import-budgets-instructions-summary">
                    Budget Import Instructions
                </summary>
                <div class="import-instructions c-margin-left--single c-margin-top--single">
                    <div class="c-margin-bottom--single">
                        One sheet lists organization budgets, plans, and line items. Repeat parent columns on each row
                        or leave them blank to continue the same org budget or plan from the row above.
                    </div>
                    <div class="c-margin-bottom--single">
                        <strong>Required:</strong> organization (id or name), fiscal year (name), org budget name; plan
                        name when importing plans or lines; line name when importing line items.
                    </div>
                    <div class="c-margin-bottom--single">
                        Spent, committed, utilization, and plan status columns from export files are read-only and
                        ignored on import.
                    </div>
                    <div class="c-margin-bottom--single">
                        Optional plan start and end dates; if omitted, plans use the org budget (fiscal year) date
                        range.
                    </div>
                </div>
            </details>
        </div>
    `,
    styles: [
        `
            .import-budgets-instructions-summary {
                cursor: pointer;
                font-weight: bold;
            }

            .import-instructions strong {
                margin-inline-end: 0.35em;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
})
export class S25ImportBudgetsComponent {
    readonly type = input.required<ImportType>();
    readonly parentCd = input<ChangeDetectorRef>();

    private readonly budgetsTelemetry: TelemetrySession = ImportTelemetry.createSession("ImportBudgets");

    constructor(
        private readonly cd: ChangeDetectorRef,
        destroyRef: DestroyRef,
    ) {
        this.budgetsTelemetry.open();
        destroyRef.onDestroy(() => this.budgetsTelemetry.close());
        effect(() => {
            this.type().onComplete = this.onCompleteBudgets;
        });
    }

    @Bind
    async onCompleteBudgets(rows: RowData[]) {
        const importType = this.type();
        try {
            importType.errors = [];
            importType.successMessage = "";

            if (!rows?.length) {
                importType.errors.push("No sheet data found");
                this.refresh();
                return;
            }

            const errorSet = new Set<string>();
            const request = BudgetImportSpreadsheetUtil.rowsToBudgetImport(rows, errorSet);

            if (!request.budgets.length) {
                if (!errorSet.size) {
                    errorSet.add("No valid budget rows found");
                }
                importType.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            if (errorSet.size) {
                importType.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            const response = await ImportService.importBudgets(request);
            this.applyResponse(response, importType);
            this.refresh();
        } catch (caughtError) {
            const errorData = caughtError?.data || caughtError?.error;
            if (errorData?.error) {
                importType.errors.push(errorData.error);
            } else {
                S25Util.showError(caughtError, "Failed to import budgets");
            }
            this.refresh();
        }
    }

    refresh() {
        this.cd.detectChanges();
        this.parentCd()?.detectChanges();
        ScrollLockService.unlockBodyScroll();
    }

    private applyResponse(response: ImportBudget.SaveResponseDto | undefined, importType: ImportType): void {
        if (response?.rowErrors?.length) {
            importType.errors.push(...response.rowErrors);
        }
        if (response?.error) {
            if (response.error === "no_budgets") {
                importType.errors.push("No budgets to import");
            } else {
                importType.errors.push("Error importing budgets: [" + response.error + "]");
            }
            return;
        }
        if (!importType.errors.length) {
            const parts: string[] = ["Budgets imported successfully"];
            if (response?.orgBudgetsUpserted != null) {
                parts.push(`${response.orgBudgetsUpserted} org budget(s)`);
            }
            if (response?.plansUpserted != null) {
                parts.push(`${response.plansUpserted} plan(s)`);
            }
            if (response?.lineItemsUpserted != null) {
                parts.push(`${response.lineItemsUpserted} line item(s)`);
            }
            importType.successMessage = parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(", ")})` : parts[0];
        }
    }

    async downloadTemplate() {
        await SpreadSheetService.downloadExcel(
            BudgetImportSpreadsheetUtil.buildImportTemplateRows(),
            "Budget_Import_Template.xlsx",
            "Budget Import Template",
        );
    }
}
